import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ConfirmContext = createContext(null);

// Promise-based replacement for window.confirm() — `const confirm =
// useConfirm(); if (!(await confirm('Delete this?'))) return;` is a drop-in
// swap for `if (!window.confirm('Delete this?')) return;`, but rendered as
// an in-app modal matching the rest of the UI instead of the browser's own
// dialog chrome.
export function ConfirmProvider({ children }) {
  const { t } = useTranslation();
  const [message, setMessage] = useState(null);
  const [danger, setDanger] = useState(true);
  // Optional custom button text (e.g. "Send confirmation" / "Not now") for
  // a question that isn't a plain confirm/cancel.
  const [labels, setLabels] = useState({});
  const resolveRef = useRef(null);

  const confirm = useCallback((msg, opts = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDanger(opts.danger !== false);
      setLabels({ confirm: opts.confirmLabel, cancel: opts.cancelLabel });
      setMessage(msg);
    });
  }, []);

  const settle = (result) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setMessage(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {message !== null && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 px-4"
          onClick={() => settle(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{message}</p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => settle(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {labels.cancel || t('common:actions.cancel')}
              </button>
              <button
                type="button"
                onClick={() => settle(true)}
                autoFocus
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-brand-600 hover:bg-brand-700'}`}
              >
                {labels.confirm || (danger ? t('common:actions.confirm') : t('common:actions.ok'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
